<script lang="ts">
	import { getShownRate } from '$lib/api/stripe';
	import { formatMoney, inDollars } from '$lib/utils/money';
	import Modal from '$lib/modal/v2/modal.svelte';
	import Affirm from '$lib/icons/affirm.svelte';
	import type { Stripe } from 'stripe';
	import { getAffirmRate } from '$lib/utils/affirm';
	import { getPromoOffAmount } from '$lib/utils/stripe';
	import SplitRate from './splitRate.svelte';
	import AffirmModalContent from '$lib/modals/bnplModal/affirmModalContent.svelte';

	interface Props {
		title?: string;
		customDescription?: string;
		price: Stripe.Price;
		reference?: Stripe.Price;
		promoCode?: Stripe.PromotionCode;
		isAffirm?: boolean;
		suffix?: string;
		isTrial?: boolean;
	}

	let {
		title,
		customDescription,
		price,
		reference,
		promoCode,
		isAffirm = false,
		suffix,
		isTrial = false
	}: Props = $props();

	function getDescription(price: Stripe.Price, amountOff = 0): string {
		if (!price.recurring) {
			return isAffirm ? 'at 0% APR with' : 'No recurring fees';
		}

		if (price.recurring.interval === 'year' && price.unit_amount) {
			const originalPrice = formatMoney(inDollars(price.unit_amount));

			return amountOff > 0
				? `${formatMoney(inDollars(price.unit_amount - amountOff))} for the first year,<br/>then ${originalPrice} per year thereafter`
				: `${originalPrice} billed yearly`;
		}

		return '';
	}

	function getRate(price: Stripe.Price): number {
		if (!isAffirm || price.type !== 'one_time') return getShownRate(price);

		return price.unit_amount ? getAffirmRate(price.unit_amount) : 0;
	}

	let affirmModal = $state<{ show: () => void; hide: () => void }>();

	let amount = $derived(getRate(price));
	let referRate = $derived(reference && getRate(reference));
	let dollarOff = $derived(reference ? (reference.unit_amount || 0) - (price.unit_amount || 0) : 0);

	let isMonthly = $derived(!!price.recurring || (isAffirm && price.type === 'one_time'));

	let promoOff = $derived(
		price.unit_amount && promoCode?.coupon
			? getPromoOffAmount(price.unit_amount, promoCode.coupon)
			: 0
	);
	let amountOff = $derived(Math.max(promoOff, dollarOff));

	let description = $derived(
		customDescription && customDescription.length > 0
			? customDescription
			: getDescription(price, amountOff)
	);
</script>

<div class="mb-4 flex flex-col gap-y-3">
	{#if title && title.length > 0}
		<h1 class="inline-flex items-center gap-x-2 text-[20px]/[110%] font-semibold">
			{title}

			{#if amountOff > 0}
				<div class="inline-flex items-center justify-center gap-1 rounded bg-orange-6 p-2">
					<div class="text-sm font-normal leading-[14px] text-orange-2">
						{formatMoney(inDollars(amountOff))} Off
					</div>
				</div>
			{/if}
		</h1>
	{/if}

	<div class="flex flex-col">
		<div class="flex items-end gap-x-4">
			<!--Normal Products (e.g., buy)-->
			{#if !isTrial && !isMonthly && !isAffirm && promoOff > 0}
				<SplitRate
					rate={formatMoney(inDollars(Math.max(0, amount - amountOff)))}
					{isMonthly}
					{suffix}
				/>
				<SplitRate strikeThrough rate={formatMoney(inDollars(amount))} {isMonthly} />
				<!--If a referral product is given -->
			{:else if !isTrial && !isMonthly && !isAffirm && referRate && dollarOff > 0}
				<SplitRate rate={formatMoney(inDollars(amount))} {isMonthly} {suffix} />
				<SplitRate strikeThrough rate={formatMoney(inDollars(referRate))} {isMonthly} />
				<!--Trial Products (e.g., RCity)-->
			{:else if !isAffirm && (isTrial || isMonthly)}
				<SplitRate rate={formatMoney(inDollars(amount))} {isMonthly} {suffix} />
			{:else}
				<SplitRate
					rate={formatMoney(inDollars(amount - getAffirmRate(amountOff)))}
					{isMonthly}
					{suffix}
				/>
			{/if}
		</div>
		<div class="mt-1 min-h-5 text-[14px]/[140%]">
			{#if !price.recurring && isAffirm}
				<button
					class="-m-2 flex p-2 text-left underline"
					type="button"
					onclick={(e) => {
						e.stopPropagation();
						affirmModal?.show();
					}}
				>
					{description}
					<Affirm class="mx-2 inline h-5 flex-shrink-0" />
				</button>
			{:else}
				<p class="flex items-center">
					{@html description}
				</p>
			{/if}
		</div>
	</div>

	{#if isAffirm}
		<Modal withDesktopPadding size="regular" modalName="Affirm Modal" bind:this={affirmModal}>
			<AffirmModalContent />
		</Modal>
	{/if}
</div>
